/**
 * The HTTP client (docs/07-api-design.md).
 *
 * One place that knows about the access token, the refresh dance and the
 * error envelope, so no component has to.
 *
 * S-04: the refresh token lives in a secure HTTP-only cookie the browser
 * manages. This code never sees it; it only calls /auth/refresh and gets a
 * new access token back.
 */

import type {
  ApiError,
  AuditEntry,
  BulkUserImportResult,
  ExportFilters,
  ExportRecord,
  Isolate,
  IsolatePage,
  IsolatePayload,
  QAFlag,
  ReferenceBundle,
  SessionRecord,
  SystemHealth,
  Team,
  TeamDashboard,
  User,
} from "./types";

const BASE = "/api/v1";

let accessToken: string | null = null;
/** Called when the session ends for good, so the app can show the login screen. */
let onSessionLost: (() => void) | null = null;
/** De-duplicates concurrent refreshes: many 401s, one refresh call. */
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

export class ApiRequestError extends Error implements ApiError {
  error: string;
  detail?: Record<string, unknown> | null;
  status: number;

  constructor(body: ApiError, status: number) {
    super(body.message || "Request failed");
    this.name = "ApiRequestError";
    this.error = body.error ?? "internal_error";
    this.detail = body.detail ?? null;
    this.status = status;
  }

  /** True when the request failed because the device is offline. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Set for the refresh call itself, to avoid an infinite loop. */
  skipRefresh?: boolean;
  raw?: boolean;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiError;
    if (body && typeof body.message === "string") return body;
  } catch {
    // fall through to a generic shape
  }
  return { error: "internal_error", message: response.statusText || "Request failed" };
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all see this result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipRefresh, raw, headers, ...rest } = options;

  const send = async (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        ...(body !== undefined && !(body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      body:
        body instanceof FormData
          ? body
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
    });

  let response: Response;
  try {
    response = await send();
  } catch {
    // A network failure is not a server error: the caller should queue the
    // work rather than show a red banner (docs/09 §9.1, principle 2).
    throw new ApiRequestError(
      { error: "offline", message: "Could not reach the server." },
      0,
    );
  }

  // The access token lasts 15 minutes; a 401 usually just means it aged out.
  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(await parseError(response), response.status);
  }

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// auth (docs/07 §7.2)
// ---------------------------------------------------------------------------
export interface LoginSuccess {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  user: User;
}

export interface TotpRequired {
  totp_required: true;
  partial_token: string;
}

export const auth = {
  async login(email: string, password: string): Promise<LoginSuccess | TotpRequired> {
    const result = await request<LoginSuccess | TotpRequired>("/auth/login", {
      method: "POST",
      body: { email, password },
      skipRefresh: true,
    });
    if ("access_token" in result) setAccessToken(result.access_token);
    return result;
  },

  async verifyTotp(partialToken: string, code: string): Promise<LoginSuccess> {
    const result = await request<LoginSuccess>("/auth/totp/verify", {
      method: "POST",
      body: { partial_token: partialToken, code },
      skipRefresh: true,
    });
    setAccessToken(result.access_token);
    return result;
  },

  me: () => request<User>("/auth/me"),

  async logout(): Promise<void> {
    try {
      await request<void>("/auth/logout", { method: "POST" });
    } finally {
      setAccessToken(null);
    }
  },

  logoutEverywhere: () => request<void>("/auth/logout-all", { method: "POST" }),

  requestPasswordReset: (email: string) =>
    request<{ status: string; message: string }>("/auth/password-reset/request", {
      method: "POST",
      body: { email },
      skipRefresh: true,
    }),

  confirmPasswordReset: (token: string, newPassword: string) =>
    request<void>("/auth/password-reset/confirm", {
      method: "POST",
      body: { token, new_password: newPassword },
      skipRefresh: true,
    }),

  completeSignup: (token: string, newPassword: string, acceptConfidentiality: boolean) =>
    request<void>("/auth/signup/confirm", {
      method: "POST",
      body: {
        token,
        new_password: newPassword,
        accept_confidentiality: acceptConfidentiality,
      },
      skipRefresh: true,
    }),

  enableTotp: () =>
    request<{ secret: string; otpauth_url: string }>("/auth/totp/enable", {
      method: "POST",
    }),

  confirmTotp: (code: string) =>
    request<void>("/auth/totp/confirm", { method: "POST", body: { code } }),

  refresh: refreshAccessToken,
};

// ---------------------------------------------------------------------------
// isolates (docs/07 §7.3)
// ---------------------------------------------------------------------------
export interface IsolateQuery {
  team?: string;
  status?: string;
  from?: string;
  to?: string;
  specimen_type?: string;
  ward_code?: string;
  organism_code?: string;
  has_open_flag?: boolean;
  flag_severity?: string;
  mine?: boolean;
  q?: string;
  after?: string;
  limit?: number;
  include_total?: boolean;
}

function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export interface BulkSyncResult {
  synced: Array<{
    client_uuid: string;
    record_id: string;
    id: string;
    already_existed: boolean;
  }>;
  rejected: Array<{
    client_uuid: string;
    reason: string;
    message: string;
    existing_record_id: string | null;
    detail: Record<string, unknown> | null;
  }>;
}

export const isolates = {
  list: (query: IsolateQuery = {}) =>
    request<IsolatePage>(`/isolates${queryString(query)}`),

  get: (id: string) => request<Isolate>(`/isolates/${id}`),

  history: (id: string) => request<AuditEntry[]>(`/isolates/${id}/history`),

  create: (
    payload: IsolatePayload & {
      client_uuid: string;
      entered_offline?: boolean;
      client_created_at?: string;
      allow_blocking?: boolean;
    },
  ) => request<Isolate>("/isolates", { method: "POST", body: payload }),

  update: (
    id: string,
    changes: Partial<IsolatePayload> & {
      expected_updated_at?: string | null;
      allow_blocking?: boolean;
    },
  ) => request<Isolate>(`/isolates/${id}`, { method: "PATCH", body: changes }),

  void: (id: string, reason: string) =>
    request<Isolate>(`/isolates/${id}/void`, { method: "POST", body: { reason } }),

  duplicateSpecimen: (
    id: string,
    body: {
      client_uuid: string;
      organism_code?: string | null;
      organism_as_written?: string | null;
      isolate_number?: string | null;
      ast_results?: Record<string, string | null>;
      allow_blocking?: boolean;
    },
  ) =>
    request<Isolate>(`/isolates/${id}/duplicate-specimen`, {
      method: "POST",
      body,
    }),

  bulkSync: (rows: Array<{ client_uuid: string; payload: unknown }>) =>
    request<BulkSyncResult>("/isolates/bulk-sync", {
      method: "POST",
      body: { isolates: rows },
    }),
};

// ---------------------------------------------------------------------------
// QA flags, exports, reference data, teams, users, admin
// ---------------------------------------------------------------------------
export const qaFlags = {
  list: (query: { severity?: string; resolved?: boolean; team?: string } = {}) =>
    request<QAFlag[]>(`/qa-flags${queryString(query)}`),

  resolve: (id: string, reason: string) =>
    request<QAFlag>(`/qa-flags/${id}/resolve`, { method: "POST", body: { reason } }),

  bulkResolve: (flagIds: string[], reason: string) =>
    request<QAFlag[]>("/qa-flags/bulk-resolve", {
      method: "POST",
      body: { flag_ids: flagIds, reason },
    }),
};

export const exports = {
  preview: (filters: ExportFilters) =>
    request<{ row_count: number; blocked_by_flags: number }>("/exports/preview", {
      method: "POST",
      body: filters,
    }),

  create: (filters: ExportFilters) =>
    request<ExportRecord>("/exports", { method: "POST", body: filters }),

  list: () => request<ExportRecord[]>("/exports"),

  get: (id: string) => request<ExportRecord>(`/exports/${id}`),

  downloadUrl: (id: string) => `${BASE}/exports/${id}/download`,
  metadataUrl: (id: string) => `${BASE}/exports/${id}/metadata`,

  /**
   * Fetch the CSV with the access token attached and hand the browser a blob.
   * A plain link would not carry the Authorization header.
   */
  async download(id: string, filename: string): Promise<void> {
    const response = await request<Response>(`/exports/${id}/download`, { raw: true });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

export const reference = {
  bundle: () => request<ReferenceBundle>("/reference/bundle"),
  version: () => request<{ version: number; updated_at: string }>("/reference/version"),

  create: (table: string, body: { code: string; name: string; note: string; extra?: object }) =>
    request<{ code: string; reference_version: number }>(`/reference/${table}`, {
      method: "POST",
      body,
    }),

  update: (
    table: string,
    code: string,
    body: { code: string; name: string; note: string; extra?: object },
  ) =>
    request<{ code: string; reference_version: number }>(`/reference/${table}/${code}`, {
      method: "PATCH",
      body,
    }),

  deactivate: (table: string, code: string) =>
    request<void>(`/reference/${table}/${code}`, { method: "DELETE" }),
};

export const teams = {
  list: () => request<Team[]>("/teams"),
  dashboard: (teamId: string, days = 7) =>
    request<TeamDashboard>(`/teams/${teamId}/dashboard?days=${days}`),
  myDashboard: (days = 7, allTeams = false) =>
    request<TeamDashboard>(`/dashboard?days=${days}&all_teams=${allTeams}`),
};

export const users = {
  list: (query: { team?: string; include_inactive?: boolean } = {}) =>
    request<User[]>(`/users${queryString(query)}`),

  create: (body: { email: string; full_name: string; role: string; team_id?: string | null }) =>
    request<{ user: User; invite_token: string; invite_expires_at: string }>("/users", {
      method: "POST",
      body,
    }),

  update: (id: string, body: Partial<{ full_name: string; role: string; team_id: string | null; is_active: boolean }>) =>
    request<User>(`/users/${id}`, { method: "PATCH", body }),

  deactivate: (id: string) => request<User>(`/users/${id}/deactivate`, { method: "POST" }),

  reinvite: (id: string) =>
    request<{ user: User; invite_token: string; invite_expires_at: string }>(
      `/users/${id}/reinvite`,
      { method: "POST" },
    ),

  bulkCsv: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return request<BulkUserImportResult>("/users/bulk-csv", {
      method: "POST",
      body: formData,
    });
  },
};

export const admin = {
  health: () => request<SystemHealth>("/admin/health"),
  sessions: (activeOnly = true) =>
    request<SessionRecord[]>(`/admin/sessions?active_only=${activeOnly}`),
  revokeSession: (id: string) =>
    request<void>(`/admin/sessions/${id}/revoke`, { method: "POST" }),
  revokeUserSessions: (userId: string) =>
    request<void>(`/admin/users/${userId}/revoke-sessions`, { method: "POST" }),
  backup: () => request<{ status: string; message: string }>("/admin/backup", { method: "POST" }),
  audit: (
    query: {
      table_name?: string;
      actor_id?: string;
      action?: string;
      date_from?: string;
      date_to?: string;
      limit?: number;
    } = {},
  ) => request<AuditEntry[]>(`/admin/audit${queryString(query)}`),
};

export interface NotificationTemplate {
  code: string;
  description: string;
  channels: Array<"sms" | "whatsapp" | "email">;
  admin_only: boolean;
  variables: Record<string, string>;
  /** A rendered example, so a user can see exactly what will arrive. */
  example: string;
}

export interface NotificationPreference {
  id: string;
  template_code: string;
  channel: "sms" | "whatsapp" | "email";
  is_enabled: boolean;
  quiet_from_hour: number | null;
  quiet_to_hour: number | null;
}

export const notifications = {
  templates: () => request<NotificationTemplate[]>("/notifications/templates"),

  preferences: () => request<NotificationPreference[]>("/notifications/preferences"),

  setPreference: (body: {
    template_code: string;
    channel: string;
    is_enabled?: boolean;
    quiet_from_hour?: number | null;
    quiet_to_hour?: number | null;
  }) => request<NotificationPreference>("/notifications/preferences", { method: "PUT", body }),

  removePreference: (templateCode: string) =>
    request<void>(`/notifications/preferences/${templateCode}`, { method: "DELETE" }),

  setPhone: (phone: string, consent: boolean) =>
    request<User>("/notifications/phone", { method: "PUT", body: { phone, consent } }),

  clearPhone: () => request<User>("/notifications/phone", { method: "DELETE" }),
};

/** The SSE endpoint. EventSource cannot set headers, so the token goes in the query (docs/07 §7.8). */
export function eventStreamUrl(token: string): string {
  return `${BASE}/events?token=${encodeURIComponent(token)}`;
}

export const health = () =>
  request<{ status: string; db: string; time: string; version: string }>("/health", {
    skipRefresh: true,
  });
